import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('token'));

    useEffect(() => {
        if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            loadUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const loadUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data.user);
        } catch (error) {
            console.error('Load user error:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password });
            const { token, user } = response.data;
            
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            setToken(token);
            setUser(user);
            
            toast.success('Login successful!');
            return { success: true, user };
        } catch (error) {
            toast.error(error.response?.data?.message || 'Login failed');
            return { success: false, error: error.response?.data?.message };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        toast.success('Logged out');
    };

    const forgotPassword = async (email) => {
        try {
            await api.post('/auth/forgot-password', { email });
            toast.success('Password reset link sent to your email!');
            return { success: true };
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send reset link');
            return { success: false, error: error.response?.data?.message };
        }
    };

    const resetPassword = async (resetToken, newPassword) => {
        try {
            await api.post(`/auth/reset-password/${resetToken}`, { newPassword });
            toast.success('Password reset successfully!');
            return { success: true };
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reset password');
            return { success: false, error: error.response?.data?.message };
        }
    };

    const updatePassword = async (currentPassword, newPassword) => {
        try {
            await api.put('/auth/update-password', { currentPassword, newPassword });
            toast.success('Password updated successfully!');
            return { success: true };
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update password');
            return { success: false, error: error.response?.data?.message };
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            logout,
            forgotPassword,
            resetPassword,
            updatePassword,
            isAuthenticated: !!user,
            isAdmin: user?.role === 'admin',
            isStaff: user?.role === 'staff' || user?.role === 'admin',
            isStudent: user?.role === 'student'
        }}>
            {children}
        </AuthContext.Provider>
    );
};